// Banner Image Upload Component
import React, { useState, useRef, useEffect } from 'react';
import { storage } from '../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/imageUtils';

const BannerImageUpload = ({ 
  label, 
  value, 
  onChange, 
  device = 'desktop', // 'desktop' or 'mobile'
  language = 'th' // 'th' or 'en'
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const fileInputRef = useRef(null);

  // Update preview when value changes (for edit mode)
  useEffect(() => {
    if (value) {
      setPreview(value);
    }
  }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('ไฟล์มีขนาดใหญ่เกินไป (ไม่เกิน 5MB)');
      return;
    }

    setUploading(true);

    try {
      // Compress image
      let fileToUpload = file;
      try {
        // Desktop: 1920x400, Mobile: 1200x300
        const maxWidth = device === 'desktop' ? 1920 : 1200;
        const maxHeight = device === 'desktop' ? 400 : 300;
        fileToUpload = await compressImage(file, maxWidth, maxHeight, 0.85);
      } catch (compressError) {
        console.warn('Error compressing image, using original:', compressError);
        fileToUpload = file;
      }

      // Create unique filename
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `campaigns/banners/${device}_${language}_${timestamp}_${randomString}.${fileExtension}`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, fileToUpload);
      const downloadURL = await getDownloadURL(storageRef);

      // Update preview and call onChange
      setPreview(downloadURL);
      if (onChange) onChange(downloadURL);
      
      alert('อัพโหลดรูปภาพสำเร็จ!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('เกิดข้อผิดพลาดในการอัพโหลด: ' + error.message);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreview('');
    if (onChange) onChange('');
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      
      {/* Preview */}
      {preview && (
        <div className="relative mb-2">
          <img
            src={preview}
            alt="Banner Preview"
            className="w-full h-32 object-cover rounded-lg border border-gray-300"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/800x200?text=Banner+Preview';
            }}
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            title="ลบรูปภาพ"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
            uploading
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {uploading ? 'กำลังอัพโหลด...' : preview ? 'เปลี่ยนรูปภาพ' : 'อัพโหลดรูปภาพ'}
        </button>
        
        {preview && (
          <input
            type="url"
            value={preview}
            onChange={(e) => {
              setPreview(e.target.value);
              if (onChange) onChange(e.target.value);
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="หรือกรอก URL โดยตรง"
          />
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      <p className="text-xs text-gray-500">
        {device === 'desktop' ? 'แนะนำขนาด: 1920x400px' : 'แนะนำขนาด: 1200x300px'} • รองรับ JPG, PNG, GIF (ไม่เกิน 5MB)
      </p>
    </div>
  );
};

export default BannerImageUpload;

